/**
 * Recorded voice "sheets": a few longer recordings (e.g. all of a floor's words read as a list, or
 * a batch of coach lines) with the time range of every word and line in them. The device fetches
 * a sheet once, decodes it and plays only the ranges it needs, so a coach line and a model word are
 * spliced on the device instead of shipping a file per clip.
 *
 * A clip's key is its text, lower-cased with spaces collapsed. A line may contain the placeholders
 * {word}, {heard} (the word as misheard, "wock") and {sound}; its key keeps them, and its ranges are the parts around each placeholder, in
 * order ("listen to me: {word}." has one range before {word} and one, possibly empty, after it).
 */

export interface Range {
  /** Seconds into the sheet's audio. */
  start: number;
  end: number;
}

export interface SheetManifest {
  /** Recording settings, for regenerating (voice, model, seed). */
  voice: string;
  sheets: {
    /** Audio file, relative to the manifest. */
    file: string;
    clips: Record<string, Range[]>;
    /** For some lines (the intro story): each word with its start, in seconds from the clip's start. */
    words?: Record<string, [string, number][]>;
  }[];
}

/** One stretch of recorded audio to play: sheet index and range. */
export interface Segment {
  sheet: number;
  range: Range;
}

export const clipKey = (text: string) => text.trim().toLowerCase().replace(/\s+/g, ' ');

interface Template {
  sheet: number;
  /** Literal text between placeholders (lower case). */
  literals: string[];
  placeholders: string[];
  ranges: Range[];
  pattern: RegExp;
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Finds recorded segments for a line or word: an exact clip, or a template with its fills. */
export class SheetIndex {
  private readonly exact = new Map<string, Segment[]>();
  private readonly templates: Template[] = [];
  private readonly timed = new Map<string, [string, number][]>();

  constructor(manifest: SheetManifest) {
    manifest.sheets.forEach((sheet, index) => {
      for (const [key, words] of Object.entries(sheet.words ?? {}))
        this.timed.set(clipKey(key), words);
      for (const [key, ranges] of Object.entries(sheet.clips)) {
        const k = clipKey(key);
        const placeholders = [...k.matchAll(/\{(word|sound|heard)\}/g)].map((m) => m[1] ?? '');
        if (placeholders.length === 0) {
          this.exact.set(
            k,
            ranges.map((range) => ({ sheet: index, range })),
          );
          continue;
        }
        const literals = k.split(/\{(?:word|sound|heard)\}/);
        this.templates.push({
          sheet: index,
          literals,
          placeholders,
          ranges,
          pattern: new RegExp(`^${literals.map(escape).join('(.+?)')}$`),
        });
      }
    });
  }

  /** When each word of a recorded line starts (s from its start), for lines that have them. */
  words(text: string): readonly [string, number][] | undefined {
    return this.timed.get(clipKey(text));
  }

  /** Segments that say `text`, or undefined when any part of it has no recording. */
  find(text: string): Segment[] | undefined {
    const k = clipKey(text);
    const exact = this.exact.get(k);
    if (exact) return exact;
    for (const t of this.templates) {
      const m = t.pattern.exec(k);
      if (!m) continue;
      const fills = m.slice(1).map((fill) => this.exact.get(clipKey(fill)));
      if (fills.some((f) => f === undefined)) continue;
      const segments: Segment[] = [];
      t.literals.forEach((_, i) => {
        const range = t.ranges[i];
        if (range && range.end > range.start) segments.push({ sheet: t.sheet, range });
        const fill = fills[i];
        if (fill) segments.push(...fill);
      });
      return segments;
    }
    return undefined;
  }
}
